import React, { useState } from 'react';
import { ChevronRight, Factory, Wrench, Zap, Monitor, ArrowLeft } from 'lucide-react';
import { factoryList } from '../../data/factories';

export interface CategorySelectionData {
  factory: string;
  factoryName: string;
  serviceType: string;
  serviceTypeName: string;
  problemType: string;
  problemTypeName: string;
}

interface CategorySelectionProps {
  onSelectionComplete: (data: CategorySelectionData) => void;
  onBack?: () => void;
}

const serviceTypes = [
  {
    id: 'informatique',
    name: 'Informatique',
    icon: Monitor,
    description: 'Support technique, logiciels, réseaux'
  },
  {
    id: 'automation',
    name: 'Automation',
    icon: Wrench,
    description: 'Systèmes automatisés, contrôles industriels'
  },
  {
    id: 'electricite',
    name: 'Électricité',
    icon: Zap,
    description: 'Installation électrique, maintenance'
  }
];

const problemTypes = {
  informatique: [
    { id: 'reseau', name: 'Problème de réseau', description: 'Connexion internet, Wi-Fi, serveurs' },
    { id: 'logiciel', name: 'Problème logiciel', description: 'Applications, système d\'exploitation' },
    { id: 'materiel', name: 'Problème matériel', description: 'Ordinateurs, imprimantes, périphériques' },
    { id: 'securite', name: 'Sécurité informatique', description: 'Virus, accès, mots de passe' },
    { id: 'autre_info', name: 'Autre problème informatique', description: 'Autres questions techniques' }
  ],
  automation: [
    { id: 'plc', name: 'Problème PLC/Automate', description: 'Programmation, configuration automates' },
    { id: 'capteurs', name: 'Capteurs/Actionneurs', description: 'Détecteurs, vérins, moteurs' },
    { id: 'hmi', name: 'Interface HMI', description: 'Écrans tactiles, supervision' },
    { id: 'communication', name: 'Communication industrielle', description: 'Protocoles, réseaux industriels' },
    { id: 'autre_auto', name: 'Autre problème automation', description: 'Autres systèmes automatisés' }
  ],
  electricite: [
    { id: 'panne', name: 'Panne électrique', description: 'Coupure, disjoncteur, fusible' },
    { id: 'installation', name: 'Installation électrique', description: 'Nouveaux équipements, câblage' },
    { id: 'maintenance', name: 'Maintenance préventive', description: 'Vérification, entretien' },
    { id: 'eclairage', name: 'Éclairage', description: 'Ampoules, néons, éclairage extérieur' },
    { id: 'autre_elec', name: 'Autre problème électrique', description: 'Autres questions électriques' }
  ]
};

type Step = 'factory' | 'service' | 'problem';

const CategorySelection: React.FC<CategorySelectionProps> = ({ onSelectionComplete, onBack }) => {
  const [currentStep, setCurrentStep] = useState<Step>('factory');
  const [selectedFactory, setSelectedFactory] = useState<string>('');
  const [selectedFactoryName, setSelectedFactoryName] = useState<string>('');
  const [selectedService, setSelectedService] = useState<string>('');
  const [selectedServiceName, setSelectedServiceName] = useState<string>('');

  const handleFactorySelect = (factoryId: string, factoryName: string) => {
    setSelectedFactory(factoryId);
    setSelectedFactoryName(factoryName);
    setCurrentStep('service');
  };

  const handleServiceSelect = (serviceId: string, serviceName: string) => {
    setSelectedService(serviceId);
    setSelectedServiceName(serviceName);
    setCurrentStep('problem');
  };

  const handleProblemSelect = (problemId: string, problemName: string) => {
    onSelectionComplete({
      factory: selectedFactory,
      factoryName: selectedFactoryName,
      serviceType: selectedService,
      serviceTypeName: selectedServiceName,
      problemType: problemId,
      problemTypeName: problemName
    });
  };

  const handleBack = () => {
    if (currentStep === 'service') {
      setCurrentStep('factory');
      setSelectedFactory('');
      setSelectedFactoryName('');
    } else if (currentStep === 'problem') {
      setCurrentStep('service');
      setSelectedService('');
      setSelectedServiceName('');
    } else if (onBack) {
      onBack();
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'factory':
        return 'Sélectionnez votre usine';
      case 'service':
        return 'Type de service requis';
      case 'problem':
        return 'Type de problème';
      default:
        return '';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 'factory':
        return 'Dans quelle usine avez-vous besoin de service ?';
      case 'service':
        return `Service requis pour ${selectedFactoryName}`;
      case 'problem':
        return `Problème ${selectedServiceName.toLowerCase()} à ${selectedFactoryName}`;
      default:
        return '';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header avec navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {(currentStep !== 'factory' || onBack) && (
            <button
              onClick={handleBack}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Retour"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          )}
          <div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              {getStepTitle()}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {getStepDescription()}
            </p>
          </div>
        </div>

        {/* Indicateur d'étapes */}
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${currentStep === 'factory' ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`} />
          <div className={`w-2 h-2 rounded-full ${currentStep === 'service' ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`} />
          <div className={`w-2 h-2 rounded-full ${currentStep === 'problem' ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`} />
        </div>
      </div>

      {/* Contenu selon l'étape */}
      {currentStep === 'factory' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {factoryList.map((factory) => (
            <button
              key={factory.id}
              onClick={() => handleFactorySelect(factory.id, factory.name)}
              className="p-4 text-left bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary hover:shadow-md transition-all duration-200 group"
            >
              <div className="flex items-center justify-between mb-2">
                <Factory className="w-5 h-5 text-primary" />
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1 text-sm sm:text-base">
                {factory.name}
              </h4>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                {factory.description}
              </p>
            </button>
          ))}
        </div>
      )}

      {currentStep === 'service' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {serviceTypes.map((service) => {
            const IconComponent = service.icon;
            return (
              <button
                key={service.id}
                onClick={() => handleServiceSelect(service.id, service.name)}
                className="p-4 text-left bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-center justify-between mb-2">
                  <IconComponent className="w-5 h-5 text-primary" />
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                </div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-1 text-sm sm:text-base">
                  {service.name}
                </h4>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  {service.description}
                </p>
              </button>
            );
          })}
        </div>
      )}

      {currentStep === 'problem' && selectedService && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {problemTypes[selectedService as keyof typeof problemTypes]?.map((problem) => (
            <button
              key={problem.id}
              onClick={() => handleProblemSelect(problem.id, problem.name)}
              className="p-4 text-left bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary hover:shadow-md transition-all duration-200 group"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="w-2 h-2 bg-primary rounded-full" />
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1 text-sm sm:text-base">
                {problem.name}
              </h4>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {problem.description}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategorySelection;